/* eslint-disable react/prop-types */
import React, {useMemo, useState} from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';

const COLORS = ['#58B8E6', '#23b563', '#f1c40f', '#e74c3c', '#9b59b6'];

const CHART_TYPES = [
  {'text': 'Barras', 'value': 'bar'},
  {'text': 'Línea', 'value': 'line'},
  {'text': 'Pastel', 'value': 'pie'},
];

const SORT_OPTIONS = [
  {'text': 'Alfabético', 'value': 'alphabetically'},
  {'text': 'Numérico', 'value': 'numerically'},
];

const WtsMonthlyActivitiesChart = ({records, serviceTypes, year}) => {
  const [chartType, setChartType] = useState('pie');
  const [sortType, setSortType] = useState('alphabetically');

  const chartData = useMemo(() => {
    if (!records || !serviceTypes) {
      return [];
    }
    const months = [
      'ene', 'feb', 'mar', 'abr', 'may', 'jun',
      'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
    ];
    const data = serviceTypes.map((st) => {
      const record = records.find(
        (r) => String(r.id_tipo_trabajo) === String(st.id)
      );
      let total = 0;
      if (record) {
        months.forEach((m) => {
          total += parseInt(record[m], 10) || 0;
        });
      }
      return {'name': st.label, 'value': total};
    }).filter((item) => item.value > 0);
    return data;
  }, [records, serviceTypes]);

  const sortedData = useMemo(() => {
    if (sortType === 'numerically') {
      return [...chartData].sort((a, b) => b.value - a.value);
    }
    return [...chartData].sort(
      (a, b) => a.name.localeCompare(b.name)
    );
  }, [chartData, sortType]);

  if (chartData.length === 0) {
    return null;
  }

  const tooltipStyle = {
    'borderRadius': '8px',
    'border': '1px solid #eef5fb',
  };

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer>
            <BarChart data={sortedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={{'fontSize': 12}}
              />
              <YAxis />
              <Tooltip
                formatter={(value) => [
                  `${value} Tareas`, 'Total',
                ]}
                contentStyle={tooltipStyle}
              />
              <Legend />
              <Bar dataKey="value" name="Tareas" fill="#58B8E6">
                {sortedData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer>
            <LineChart data={sortedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={{'fontSize': 12}}
              />
              <YAxis />
              <Tooltip
                formatter={(value) => [
                  `${value} Tareas`, 'Total',
                ]}
                contentStyle={tooltipStyle}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="value"
                name="Tareas"
                stroke="#58B8E6"
                strokeWidth={2}
                dot={{'r': 5}}
                activeDot={{'r': 7}}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'pie':
      default:
        return (
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={sortedData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({name, percent}) =>
                  `${name} (${(percent * 100).toFixed(0)}%)`
                }
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {sortedData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => [
                  `${value} Tareas`, 'Total',
                ]}
                contentStyle={tooltipStyle}
              />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        );
    }
  };

  const containerStyle = {
    'marginTop': '30px',
    'padding': '20px',
    'background': '#f9fafb',
    'borderRadius': '10px',
  };

  const titleStyle = {
    'textAlign': 'center',
    'color': '#1a1a2e',
    'marginBottom': '20px',
  };

  const controlsStyle = {
    'display': 'flex',
    'gap': '16px',
    'marginBottom': '16px',
    'alignItems': 'center',
    'flexWrap': 'wrap',
  };

  const controlGroupStyle = {
    'display': 'flex',
    'alignItems': 'center',
    'gap': '8px',
  };

  const labelStyle = {
    'fontWeight': 600,
    'fontSize': '14px',
  };

  const selectStyle = {
    'padding': '6px 12px',
    'borderRadius': '6px',
    'border': '1px solid #ccc',
    'fontSize': '14px',
  };

  return (
    <div style={containerStyle}>
      <h3 style={titleStyle}>
        Distribución de Tareas - {year}
      </h3>
      <div style={controlsStyle}>
        <div style={controlGroupStyle}>
          <label style={labelStyle}>Tipo Gráfico:</label>
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
            style={selectStyle}
          >
            {CHART_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.text}
              </option>
            ))}
          </select>
        </div>
        {chartType !== 'pie' && (
          <div style={controlGroupStyle}>
            <label style={labelStyle}>Ordenar:</label>
            <select
              value={sortType}
              onChange={(e) => setSortType(e.target.value)}
              style={selectStyle}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.text}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div style={{'width': '100%', 'height': 300}}>
        {renderChart()}
      </div>
    </div>
  );
};

export default WtsMonthlyActivitiesChart;
